import { ApplicationTheme } from "@multiweb/uiShared/ApplicationTheme";
import type { Core, WebViewerInstance } from "@pdftron/webviewer";
import type WebViewer from "@pdftron/webviewer";
import { Event as GlobalEvent } from "Global/Base/Events";
import { Deferred, PromiseUtils } from "Global/Base/Promises/PromiseUtils";
import { EnumUtils } from "Global/Base/Utils/EnumUtils";
import { Globalization } from "Global/Dom/Globalization";
import { Localization } from "OW.Remoting/Remoting/Localization";
import { GenericControl } from "UI/UI.BaseControls/GenericControl";
import { FileEditElement } from "UI/UI.Controls/FileEditElement";
import { IFileUploadStatusChangeEvent } from "UI/UI.Controls/IEvent";
import { SimpleFileEditElement } from "UI/UI.Controls/SimpleFileEditElement";
import { createElement, registerCustomElement } from "UI/UI.ElementarControls/Utils/CustomElementUtils";
import { FileEditStatus } from "UI/UI/UI/FileMenu";
import { UIManager } from "../../UIManager";
import { PDF_TRON_LICENCE_KEY } from "../Constants";
import { PdfEditFeatureGroup, PdfEditFeatures } from "./PdfEditFeatures";

export class PdfEditElement extends HTMLElement {
    public documentUpdate: GlobalEvent<PdfEditElement, undefined> = new GlobalEvent;
    private _annotationUserName: string | null = null;

    public get annotationUserName(): string {
        return this._annotationUserName;
    }

    public set annotationUserName(v: string) {
        this._annotationUserName = v;

        this._pdfTronInstance?.Core.annotationManager?.setCurrentUser(v);
    }

    private _features: string[];

    protected _fileId = 0;
    protected _fileEdit: FileEditElement;
    protected _previewContainer: HTMLElement;
    protected _resizeBtn: HTMLElement;
    protected _imageTestElement: HTMLImageElement;
    protected _iframeElement: HTMLIFrameElement;

    private _errorDescriptionElement: HTMLElement;
    protected _enabled = false;
    private _lastFileUrl: string = null;

    protected _status: FileEditStatus;
    protected _imgAddr: string;
    protected _errorSrc: boolean; // pokud byl pri uploadu error, aby zustal i po dokonceni a uploadu a zmeny stavu na fileeditu

    private _pdfTronInstance: WebViewerInstance;

    public hasObject = false;

    private _mutationObs: MutationObserver;

    private _fullWebViewerInitialization: Deferred<void> = PromiseUtils.defer<void>();
    private _widthMO: MutationObserver;

    //#region Properties
    public set visible(v: boolean) {
        this.classList.toggle("Hidden", !v);
    }

    public get visible(): boolean {
        return !this.classList.contains("Hidden");
    }

    public set enabled(v: boolean) {
        this._fileEdit.enabled = v;
        this.classList.toggle("disabled", !v);
        this._enabled = v;

        if (!v) {
            this.inEditMode = false;
        }
    }

    public get enabled(): boolean {
        return this._enabled;
    }

    public set inEditMode(v: boolean) {
        if (!this._enabled) {
            return;
        }

        if (!v) {
            this._pdfTronInstance.Core.annotationManager.enableReadOnlyMode();
        } else {
            this._pdfTronInstance.Core.annotationManager.disableReadOnlyMode();
        }

        this._fileEdit.visible = v;
    }

    public set src(v: string) {
        this._imgAddr = v;
        this._errorSrc = false;
        if (v) {
            const old = this._lastFileUrl;
            this._lastFileUrl = v;
            this._fullWebViewerInitialization.promise.then(async () => {
                if (!old) {
                    await this._pdfTronInstance.UI.closeDocument();
                }
                this._pdfTronInstance.UI.loadDocument(v, { filename: this._fileEdit.fileName });
            });

            if (this._fileHasPDFExtension) {
                this.setStatus(FileEditStatus.download);
            } else {
                this.setStatus(FileEditStatus.previewNotSupported);
            }
        } else {
            if (this.enabled) {
                this.setStatus(FileEditStatus.empty);
            } else {
                this.setStatus(FileEditStatus.error);
            }
        }
    }

    public get src(): string {
        return this._imgAddr;
    }

    public get fileEdit(): FileEditElement {
        return this._fileEdit;
    }

    private get _fileHasPDFExtension(): boolean {
        return this._fileEdit.fileName?.toLocaleUpperCase().endsWith(".PDF") ?? false;
    }

    public get features(): string[] {
        return this._features;
    }

    public set features(value: string[]) {
        this._features = value;
    }
    //#endregion Properties

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        GenericControl._constructor(this as HTMLElement);
        this._errorSrc = false;
        this._fileEdit = new FileEditElement();
        this._fileEdit.visible = false;
        this.setStatus(FileEditStatus.unavailable);

        this._createElements();

        this._widthMO = new MutationObserver((mutations) => {
            mutations.forEach(mutation => {
                if (mutation.attributeName === "style") {
                    this._initFileEditSize();
                }
            });
        });
    }

    public setStatus(status: FileEditStatus): void {
        this._status = status;
        this.classList.remove("Error", "Uploading", "Empty", "Download", "Finishing", "NotImage", "FileSizeExceed", "Unavailable");
        if (this._errorDescriptionElement) {
            this._errorDescriptionElement.innerHTML = "";
        }

        if (this._status == FileEditStatus.empty) {
            this.classList.add("Empty");
        } else if (this._status == FileEditStatus.error) {
            this.classList.add("Error");
        } else if (this._status == FileEditStatus.previewNotSupported) {
            this.classList.add("Error", "NotImage");
        } else if (this._status == FileEditStatus.fileSizeExceed) {
            this.classList.add("Error", "FileSizeExceed");
            Localization.addLocTxtData(this._errorDescriptionElement, "GUI-729327");
        } else if (this._status == FileEditStatus.uploading) {
            this.classList.add("Uploading");
        } else if (this._status == FileEditStatus.download) {
            this.classList.add("Download");
        } else if (this._status == FileEditStatus.unavailable) {
            this.classList.add("Error", "Unavailable");
        }
        this._initFileEditSize();
    }

    /**
     * Vytvori velké náhledové okno
     */
    protected _createImageWindow(): void {
        const imgWindow = createElement({ classes: "fullImagePreview" });
        const prevWindow = createElement({ classes: "previewContainer" });

        const image = createElement({ type: "img", classes: "image", value: this.src }) as HTMLImageElement;
        const toolbarImg = createElement({ classes: "toolbar" });
        const btn = createElement({ classes: "closeBtn" });
        let windowKeyEvent: any;

        const unregisterFnc = (): void => {
            GenericControl.unregisterEventListener(btn);
            GenericControl.unregisterEventListener(prevWindow);
            window.removeEventListener("keyup", windowKeyEvent);
        };
        toolbarImg.appendChild(btn);

        imgWindow.appendChild(toolbarImg);
        imgWindow.appendChild(prevWindow);
        imgWindow.appendChild(image);

        GenericControl.registerEventListener(btn, "click", () => {
            unregisterFnc();
            imgWindow.remove();
        });

        GenericControl.registerEventListener(prevWindow, "click", () => {
            unregisterFnc();
            imgWindow.remove();
        });

        window.addEventListener("keyup", windowKeyEvent = (e: KeyboardEvent) => {
            if (e.code === "Escape") { //ESC
                unregisterFnc();
                imgWindow.remove();
            }
        });

        document.body.appendChild(imgWindow);
    }

    protected _createElements(): void {
        this.classList.add("pdf-edit-element");
        this._previewContainer = createElement({ classes: "previewContainer" });
        const toolbarImg = createElement({ classes: "toolbar" });
        this._iframeElement = createElement({ type: "div", classes: "imagePreview" }) as HTMLIFrameElement;
        this._imageTestElement = document.createElement("img");
        this._resizeBtn = createElement({ classes: "resizeBtn" });
        const noimagespan = createElement({ type: "span", classes: "errorText" });

        const statusContainer = createElement({ classes: "status-container" });

        const statusNoFile = createElement({ classes: "no-file" });
        const uploadFile = createElement({ classes: "upload-file drop-zone" });
        const loadingFile = createElement({ classes: "loading-file" });

        const uploadTextDiv = createElement({ classes: "bottom text" });
        const noFileTextDiv = createElement({ classes: "bottom text" });
        this._errorDescriptionElement = createElement({ classes: "bottom text description" });

        uploadFile.innerHTML =
        "<div class=\"top icon\"><img src='img/pdfviewer/pdf_icon_light.png' alt='Ikona' /></div>";

        loadingFile.innerHTML = `<div class='loading'><div class="loader">
        <div class="right">
            <div class="border"></div>
        </div>
        <div class="left">
            <div class="border"></div>
        </div>
    </div></div>`;

        uploadFile.appendChild(uploadTextDiv);
        statusNoFile.insertAdjacentHTML("afterbegin", "<div class=\"top icon\"></div>");

        statusNoFile.appendChild(noFileTextDiv);
        statusNoFile.appendChild(this._errorDescriptionElement);

        //GUI-720982 - Přetáhněte sem PDF soubor nebo ho vyberte z počítače.
        const uploadSubText = createElement({ type: "span" });
        const uploadSubText2 = createElement({ type: "span", classes: "fake-link" });
        // GUI-722904 - Přetáhněte sem PDF soubor nebo ho
        // GUI-722905 - vyberte z počítače
        Localization.addLocTxtData(uploadSubText, "GUI-722904");
        Localization.addLocTxtData(uploadSubText2, "GUI-722905");

        uploadTextDiv.insertAdjacentElement("beforeend", uploadSubText);
        uploadTextDiv.insertAdjacentHTML("beforeend", "&nbsp;");
        uploadTextDiv.insertAdjacentElement("beforeend", uploadSubText2);

        //Localization.addLocTxtData(uploadTextDiv, "GUI-720982");

        //GUI-720985 - Náhled není k dispozici
        Localization.addLocTxtData(noFileTextDiv, "GUI-720985");

        // GUI-729327 - Překročena maximální velikost souboru pro náhled
        //Localization.addLocTxtData(this._errorDescriptionElement, "GUI-729327");

        statusContainer.appendChild(statusNoFile);
        statusContainer.appendChild(uploadFile);
        statusContainer.appendChild(loadingFile);

        Localization.getTextPromise("GUI-601172").then((r) => {
            noimagespan.textContent = r;
        });

        this._fileEdit.mask = "application/pdf";

        toolbarImg.appendChild(this._resizeBtn);

        this._previewContainer.appendChild(toolbarImg);
        this._previewContainer.appendChild(this._iframeElement);
        this._previewContainer.appendChild(noimagespan);

        this.appendChild(statusContainer);
        this.appendChild(this._previewContainer);
        this.appendChild(this._fileEdit);

        this._fileEdit.dropzone = uploadFile;

        uploadFile.addEventListener("click", () => {
            if (this.hasObject) {
                this._fileEdit.click();
            }
        });
    }

    private async _initWebViewer(): Promise<void> {
        const webViewer: typeof WebViewer = (await import("@pdftron/webviewer")).default;
        const instance = await webViewer({
            path: "modules/pdfviewer", // path to the PDFTron 'lib' folder on your server
            licenseKey: PDF_TRON_LICENCE_KEY,
            fullAPI: true,
            initialDoc: null,
            enableRedaction: true,
            isReadOnly: true,
            annotationUser: this.annotationUserName,
            css: "./css/pdfviewer/theme.css",
            config:"modules/pdfviewer/config.js",
            useDownloader: false, // zakaze linearizovane stahovani, velke PDF dokumenty se mohou zobrazovat pomaleji, ale je potreba doplnit podporu na DMS
        }, this._iframeElement);

        this._pdfTronInstance = instance;
        this._fullWebViewerInitialization.resolve();
        this._initWebViewerInstance(instance);
    }

    private _initWebViewerInstance(instance: WebViewerInstance): void {
        const updateTheme = (instance: WebViewerInstance) => {
            if (UIManager.instance.applicationTheme == ApplicationTheme.dark) {
                instance.UI.iframeWindow.document.documentElement.setAttribute("data-theme", "mw-dark");
            } else {
                instance.UI.iframeWindow.document.documentElement.setAttribute("data-theme", "mw-light");
            }
        };
        const { documentViewer, annotationManager } = instance.Core;

        instance.UI.disableElements([ "downloadButton", "saveAsButton" ]);

        const features = new PdfEditFeatures();

        // Povolení základních ovládacích prvků
        features.enableFeatureGroup(PdfEditFeatureGroup.View);
        features.enableFeatureGroup(PdfEditFeatureGroup.ViewControls);
        features.enableFeatureGroup(PdfEditFeatureGroup.Thumbnails);
        features.enableFeatureGroup(PdfEditFeatureGroup.PageNavigation);
        features.enableFeatureGroup(PdfEditFeatureGroup.Zoom);
        features.enableFeatureGroup(PdfEditFeatureGroup.Search);
        features.enableFeatureGroup(PdfEditFeatureGroup.FullScreen);
        features.enableFeatureGroup(PdfEditFeatureGroup.Print);

        // Povolení ovládacích prvků nastavených v definici formu.
        if (this._features) {
            for (const feature of this.features) {
                features.enableFeatureGroup(EnumUtils.getEnumValueFromName(PdfEditFeatureGroup, feature, false));
            }
        }

        // Schování všech nepovolených ovládacích prvků.
        instance.UI.disableElements(features.disabledElements);

        // Nastaveni jazyka
        const actualCulture = Globalization.actualCulture;
        const cultSeparator = actualCulture.indexOf("-");
        const cult = cultSeparator !== -1 ? actualCulture.substring(0, cultSeparator) : actualCulture;

        instance.UI.setLanguage(cult);

        documentViewer.addEventListener("mouseLeftUp", (_e) => {
            this.click();
        });

        updateTheme(instance);

        // TODO Odstranit pri zpracovani vlastniho WebUI, nevraci to zadne chybove hlasky nic.
        const errModal = instance.UI.iframeWindow.document.querySelector(".ErrorModal");
        this._mutationObs = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.target == document.documentElement) {
                    updateTheme(instance);
                } else {
                    if (mutation.type == "attributes") {
                        if (errModal.classList.contains("open")) {
                            const tc = errModal.textContent as string;
                            if (tc.startsWith("File extension") && tc.includes("is not supported")) {
                                this.setStatus(FileEditStatus.error);
                            }
                        }
                    }
                }
            }
        });

        const annotUpdateListener = (_annotations: Array<Core.Annotations.Annotation>, _action: any, _info: any) => {
            this.documentUpdate.trigger(this, null);
        };

        documentViewer.addEventListener("annotationsLoaded", () => {
            annotationManager.addEventListener("annotationChanged", annotUpdateListener);
        });

        documentViewer.addEventListener("documentUnloaded", () => {
            annotationManager.removeEventListener("annotationChanged", annotUpdateListener);
        });

        if (errModal) {
            this._mutationObs.observe(errModal, { attributes: true, attributeFilter: [ "class" ] });
        }
        this._mutationObs.observe(document.documentElement, { attributes: true, attributeFilter: [ "class" ] });
    }

    private _attachWebViewer(): void {
        this._initWebViewer();
    }

    private _dettachWebViewer(): void {
        this._fullWebViewerInitialization = PromiseUtils.defer<void>();
        //this._pdfTronInstance?.dispose();
        this._pdfTronInstance = null;
        if (this._iframeElement) {
            this._iframeElement.innerHTML = "";
        }
        this._mutationObs?.disconnect();
    }

    private _initFileEditSize(): void {
        let width = parseInt(this.parentElement?.style.width);

        if (isNaN(width) || width === 0) {
            width = this._iframeElement?.offsetWidth ?? 0;
        } else {
            width -= 2;
        }

        if (width === 0) {
            width = this.offsetWidth;
        }
        this.classList.toggle("small", width <= 330);
    }

    public async getDocument(): Promise<File> {
        const { documentViewer, annotationManager } = this._pdfTronInstance.Core;
        const doc = documentViewer.getDocument();
        const xfdfString = await annotationManager.exportAnnotations(null);
        const data = await doc.getFileData({
            xfdfString,
        });

        const arr = new Uint8Array(data);

        return new File([arr], this._fileEdit.fileName ?? `Soubor${this._fileId++}.pdf`, { type: "application/pdf" });
    }

    protected connectedCallback(): void {
        (this._fileEdit as SimpleFileEditElement).registerEventListener("uploadRequest", (e) => {
            if (e.detail.files.length > 0) {
                this.setStatus(FileEditStatus.uploading);
            }
        });

        this._fileEdit.registerEventListener("statusChange", (e: IFileUploadStatusChangeEvent) => {
            if (this._errorSrc && e.detail.status == FileEditStatus.download) {
                this.setStatus(FileEditStatus.error);
            } else if (this.fileEdit.fileName && !this._fileHasPDFExtension && e.detail.status === FileEditStatus.download) {
                this.setStatus(FileEditStatus.previewNotSupported);
                this._errorSrc = true;
            } else {
                this.setStatus(e.detail.status);
            }
        });

        GenericControl.registerEventListener(this._iframeElement, "error", (_e) => {
            this.setStatus(FileEditStatus.previewNotSupported);
        });

        GenericControl.registerEventListener(this._imageTestElement, "error", (_e) => {
            this.setStatus(FileEditStatus.previewNotSupported);
            this._errorSrc = true;
        });

        this._attachWebViewer();

        this._initFileEditSize();

        this._widthMO.observe(this.parentElement, { attributes: true, attributeFilter: [ "style" ] });
    }

    protected disconnectedCallback(): void {
        this.unregisterEventListener();
        GenericControl.unregisterEventListener(this._previewContainer);
        GenericControl.unregisterEventListener(this._iframeElement);
        this._dettachWebViewer();

        this._widthMO.disconnect();
    }

    protected attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        GenericControl.attributeChangedCallback(this as HTMLElement, attrName, oldVal, newVal);
    }

    //#region Zpracování metod z globální třídy GenericControl
    /**
     * Zaregistruje událost
     * @param event Název události
     * @param action Akce při vyvolání události
     */
    public registerEventListener(event: string, action: (e: Event) => void): void {
        GenericControl.registerEventListener(this as HTMLElement, event, action);
    }

    /**
     * Odregistruje udalost. Pokud neni vyplnen zadny parametr, jsou odregistrovany vsechny udalosti. Pokud je vyplnen pouze event, odregistruje vsechny udalosti daneho typu
     * pokud je vyplnen event a action, odregistruje pouze konkretni akci
     * @param event Event, ktery chcete odregistrovat.
     * @param action Akce, ktere se tyka event a chcete ji odregistrovat
     */
    public unregisterEventListener(event?: string, action?: (e: Event) => void): void {
        GenericControl.unregisterEventListener(this as HTMLElement, event, action);
    }

    /**
     * Zavolá událost na elementu
     * @param eventName Název události
     * @param detail Podrobnosti události
     * @return Vrátí customEvent
     */
    public callEvent(eventName: string, detail?: any, bubbles = false, cancelable = false): CustomEvent {
        return GenericControl.callEvent(this as HTMLElement, eventName, detail, bubbles, cancelable);
    }

    /**
     * Vrátí pozici elementu v rámci dokumentu
     */
    public offset(): { top: number; left: number } {
        return GenericControl.offset(this as HTMLElement);
    }

    //#enregion Zpracování metod z globální třídy GenericControl
}

registerCustomElement("tsc-PdfEdit", PdfEditElement);
